import React from "react";
import { niceDate, yearFromNow } from "../../util/util";

const styles = {
  app: {
    fontFamily: "Arial, sans-serif",
    maxWidth: "800px",
    margin: "0 auto",
    padding: "20px",
  },
  header: {
    textAlign: "center",
    marginBottom: "20px",
  },
  hospitalName: {
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "5px",
  },
  branch: {
    fontSize: "18px",
    marginBottom: "5px",
  },
  openVision: {
    fontSize: "16px",
    fontStyle: "italic",
  },
  section: {
    border: "2px solid #000",
    padding: "10px",
    marginBottom: "20px",
  },
  flexContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  flexItem: {
    width: "49%",
    border: "1px solid #000",
    padding: "10px",
  },
  checklistTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "10px",
  },
  checklistSubtitle: {
    fontSize: "14px",
    fontStyle: "italic",
    textAlign: "center",
    marginBottom: "15px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "bold",
    marginBottom: "10px",
  },
  list: {
    listStyleType: "none",
    paddingLeft: "0",
    marginTop: "0",
  },
  listItem: {
    marginBottom: "5px",
  },
  signatureLine: {
    borderTop: "1px solid #000",
    marginTop: "15px",
    paddingTop: "5px",
  },
  patientInfo: {
    marginTop: "15px",
  },
  transportationRecord: {
    border: "2px solid #000",
    padding: "10px",
    marginTop: "20px",
  },
  transportationTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "15px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "15px",
  },
  tableHeader: {
    border: "1px solid #000",
    padding: "8px",
    textAlign: "left",
    fontWeight: "bold",
  },
  tableCell: {
    border: "1px solid #000",
    padding: "8px",
    textAlign: "left",
  },
};

const ChecklistItem = ({ title, subtitle, items }) => (
  <div>
    <h4 style={styles.sectionTitle}>{title}</h4>
    {subtitle && (
      <p style={{ fontStyle: "italic", marginTop: "-10px" }}>{subtitle}</p>
    )}
    <ul style={styles.list}>
      {items.map((item, index) => (
        <li key={index} style={styles.listItem}>
          <span dangerouslySetInnerHTML={{ __html: item }} />
        </li>
      ))}
    </ul>
  </div>
);

const SignatureLine = ({ label }) => (
  <div style={styles.signatureLine}>{label}</div>
);

const Checklist = (data) => {
  const {
    patientName,
    patientGenericPid,
    patientDob,
    patientSex,
    onDay,
    site,
  } = data.data;

  return (
    <section style={styles.section}>
      <u>
        <h2 style={styles.checklistTitle}>Surgical Safety Checklist</h2>
      </u>
      <p style={styles.checklistSubtitle}>
        (Adapted from the WHO Surgical Safety Checklist)
      </p>
      <div style={{ fontSize: "15px" }}>
        <p>
          Patient Name:{" "}
          <b>
            <u>{patientName}</u>
          </b>{" "}
          Age:{" "}
          <b>
            <u>{yearFromNow(patientDob)}</u>
          </b>{" "}
          Gender:{" "}
          <b>
            <u>{patientSex}</u>
          </b>
        </p>
        <p>
          PID No.:{" "}
          <b>
            <u>{patientGenericPid}</u>
          </b>{" "}
          Date:{" "}
          <b>
            <u>{niceDate(onDay)}</u>
          </b>
        </p>
        <p>
          Branch:
          <b>
            <u>{site}</u>
          </b>
        </p>
      </div>
      <div style={styles.flexContainer}>
        <div style={styles.flexItem}>
          <ChecklistItem
            title="SIGN IN (To be read out loud)"
            subtitle="Before giving Anaesthesia"
            items={[
              "Patient has confirmed:",
              "☐ Identity (Name & Address)",
              "Eye: ☐ RE ☐ LE",
              "☐ Name of Surgeon",
              "☐ Type of Surgery and IOL",
              "☐ Billing details verified",
              "☐ Ocular Investigations",
              "Any known allergy?",
              "☐ No ☐ Yes",
              "Systemic Investigations?",
              "☐ No ☐ Yes",
              "☐ Use of Premedications",
            ]}
          />
        </div>
        <div style={styles.flexItem}>
          <ChecklistItem
            title="TIME OUT (To be read out loud)"
            subtitle="Before Surgery"
            items={[
              "Surgeon and Nurse verbally confirm:",
              "☐ Patient Identity",
              "Eye: ☐ RE ☐ LE",
              "☐ Confirmation of Instrument Sterility (including indicator results)",
              "☐ Procedure",
              "☐ Lens model and power",
              "☐ Other Implants/ Tissues/ Intraoperative medications",
            ]}
          />
        </div>
      </div>

      <div style={{ ...styles.flexItem, width: "100%", marginTop: "15px" }}>
        <ChecklistItem
          title="SIGN OUT (To be read out loud)"
          subtitle="After Surgery"
          items={[
            "Nurse verbally confirms with Team:",
            "☐ Name of the procedure recorded/ Any change of procedure/ Complications",
            "☐ Any Specific post op instruction/ Medicines/ Positioning",
          ]}
        />
      </div>

      <SignatureLine label="Signature (OT Floor)" />
      <SignatureLine label="Signature (Ward)" />
      <SignatureLine label="Signature (Anaesthetist/ Physician-in-charge/ Surgeon)" />
      <SignatureLine label="Signature (Block Room)" />
    </section>
  );
};

const TransportationRecord = (data) => {
  const {
    patientName,
    patientGenericPid,
    patientDob,
    patientSex,
    patientPhone,
    patientAddress,
    onDay,
    site,
  } = data.data;
  const items = [
    "Paper",
    "Lens Box",
    "Propak Bag",
    "Viscoat Box",
    "Silicon Oil (1300/ 5000)",
    "Accentrix Box",
    "Razumab Box",
    "Lucentis Box",
    "Eylea Box",
    "PFCL Box",
    "Intervitreal PageNax",
    "Intervitreal Ozurdex",
  ];

  return (
    <section style={styles.transportationRecord}>
      <u>
        {" "}
        <h2 style={styles.transportationTitle}>
          Patient KIT Transportation Record
        </h2>
      </u>
      <div style={{ fontSize: "15px" }}>
        <p>
          Patient Name:{" "}
          <b>
            <u>{patientName}</u>
          </b>{" "}
          Age:{" "}
          <b>
            <u>{yearFromNow(patientDob)}</u>
          </b>{" "}
          Gender:{" "}
          <b>
            <u>{patientSex}</u>
          </b>
        </p>
        <p>
          PID No.:{" "}
          <b>
            <u>{patientGenericPid}</u>
          </b>{" "}
          Date:{" "}
          <b>
            <u>{niceDate(onDay)}</u>
          </b>
        </p>
        <p>
          Branch:
          <b>
            <u>{site}</u>
          </b>
        </p>
      </div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.tableHeader}>Sl. No.</th>
            <th style={styles.tableHeader}>Handover Details</th>
            <th style={styles.tableHeader}>OT Floor Personnel (✓)</th>
            <th style={styles.tableHeader}>Ward (✓)</th>
            <th style={styles.tableHeader}>Patient/ Relatives (✓)</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={index}>
              <td style={styles.tableCell}>{index + 1}</td>
              <td style={styles.tableCell}>{item}</td>
              <td style={styles.tableCell}></td>
              <td style={styles.tableCell}></td>
              <td style={styles.tableCell}></td>
            </tr>
          ))}
        </tbody>
      </table>
      <SignatureLine label="Acceptance Signature" />
    </section>
  );
};
const SafetyChecklistComponent = (props) => (
  <div style={styles.app}>
    <div style={{ marginBottom: "30rem" }}>
      <Checklist data={props.data} />
    </div>

    <TransportationRecord data={props.data} />
  </div>
);

export default SafetyChecklistComponent;
